import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { useGame } from './GameContext' // Added import
import { useWebSocket } from './WebSocketContext'
import { API_BASE_URL } from '../config/api'

const NotificationContext = createContext()

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

// --- Internal Hook for API Handling ---
function useApi(session) {
    const pendingRequests = useRef(new Map())

    const request = useCallback(async (endpoint, options = {}) => {
        const method = options.method || 'GET'
        const bodyStr = options.body ? JSON.stringify(options.body) : ''
        const requestKey = `${method}:${endpoint}:${bodyStr}`

        if (method === 'GET' && pendingRequests.current.has(requestKey)) {
            return pendingRequests.current.get(requestKey)
        }

        const execute = async () => {
            const url = `${API_BASE_URL}/api/v1${endpoint}`
            const headers = {
                'Content-Type': 'application/json',
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
            }

            const config = {
                ...options,
                headers: { ...headers, ...options.headers },
                body: options.body // Caller handles stringify if needed, or we do it here if object
            }

            if (config.body && typeof config.body !== 'string') {
                config.body = JSON.stringify(config.body)
            }

            const response = await fetch(url, config)
            
            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || `Request failed: ${response.status}`)
            }

            // Some endpoints might return 204 No Content
            if (response.status === 204) return null
            
            return response.json()
        }

        const promise = execute().finally(() => {
            if (method === 'GET') pendingRequests.current.delete(requestKey)
        })

        if (method === 'GET') {
            pendingRequests.current.set(requestKey, promise)
        }

        return promise
    }, [session])

    return request
}

export function NotificationProvider({ children }) {
  const { session } = useAuth()
  const { subscribe } = useWebSocket()
  const { currentGame } = useGame() // Get current game
  const api = useApi(session)
  const navigate = useNavigate()
  const location = useLocation()
  
  // Ref to hold currentGame to avoid stale closures
  const currentGameRef = useRef(currentGame)
  const activeGameToasts = useRef(new Map()) // GameID -> ToastID

  useEffect(() => {
      currentGameRef.current = currentGame
  }, [currentGame])

  // Cleanup toasts when navigating to the game
  useEffect(() => {
      activeGameToasts.current.forEach((toastId, gameId) => {
          if (location.pathname.includes(gameId)) {
               if (window.dismissToast) window.dismissToast(toastId)
               activeGameToasts.current.delete(gameId)
          }
      })
  }, [location])
  
  const [notifications, setNotifications] = useState([])
  
  // Helper for UI feedback
  const showToast = useCallback((message, type = 'info', duration = 3000, options = {}) => {
      if (window.showToast) return window.showToast(message, type, duration, options)
      else console.log(`TOAST [${type}]: ${message}`)
  }, [])

  const addNotification = useCallback((notification) => {
    setNotifications(prev => {
        // Dedup based on ID
        if (prev.some(n => n.id === notification.id)) return prev
        return [...prev, notification]
    })
  }, [])

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id))
  }, [])

  // WebSocket Subscription
  useEffect(() => {
      const handleNotification = (message) => {
        console.log('Notification: ', message)
          // Special handling for game_started event (Auto-Join)
          // Special handling for game_started event (Auto-Join)
          if (message.type === 'game_started' && message.data?.game_id) {
              // Add a small delay to allow React state updates (e.g. previous game completion) to flush
              // This prevents the "Busy" check from seeing a stale "Active" status for a just-completed game.
              setTimeout(() => {
                  const gameId = message.data.game_id
                  const currentPath = window.location.pathname
                  console.log('NOTIF DEBUG:', { type: message.type, msgId: message.id, gameId, currentPath, alreadyIn: currentPath.includes(gameId) })
                  
                  const isGamePage = currentPath.includes('/game/')
                  
                  // Smart Busy Check:
                  // If we are on a game page, check if that game is actually active.
                  // If it's completed/abandoned/cancelled, we are NOT busy.
                  let isBusy = currentPath.includes('/spectate/') 
                               //currentPath.includes('/replay/') ||
                               //currentPath.includes('/tournaments/')

                  if (isGamePage) {
                      // If we have a current game loaded and it matches the URL
                      // (Safe check: ensure we aren't looking at a stale game object)
                      const onCurrentGameUrl = currentPath.includes(currentGameRef.current?.id)
                      
                      if (onCurrentGameUrl && currentGameRef.current) {
                          const status = currentGameRef.current.status
                          const isFinished = ['completed', 'abandoned', 'cancelled'].includes(status)
                          
                          if (!isFinished) {
                              isBusy = true // Game is active or waiting/pregame -> BUSY
                          }
                          // If finished, isBusy stays false (unless other flags set it)
                      } else {
                          // On a game page but context not matching or missing? Assume busy to be safe.
                          isBusy = true
                      }
                  }

                  // Check if we are already in THIS game
                  const alreadyInGame = currentPath.includes(`/game/${gameId}`)

                  if (alreadyInGame) return

                  if (isBusy) {
                    console.log('busy - suppressing auto-join')
                      // If busy, don't auto-navigate. Show interactive toast.
                      const toastId = showToast('Game Started!', 'success', 10000, {
                          actions: [
                              {
                                  label: 'Join in New Tab',
                                  onClick: () => window.open(`/game/${gameId}`, '_blank'),
                                  primary: true
                              },
                              {
                                  label: 'Join Here',
                                  onClick: () => navigate(`/game/${gameId}`),
                                  primary: false
                              }
                          ]
                      })
                      if (toastId) activeGameToasts.current.set(gameId, toastId)
                  } else {
                      // If idle, auto-join
                      showToast('Game Started! Joining...', 'success')
                      navigate(`/game/${gameId}`)
                  }
              }, 100)
              
              return
          }

          if (message.type !== 'notification' || !message.data) return

          const data = message.data
          const type = data.type
          
          // Generate stable ID if missing
          const id = data.id || `${type}_${Date.now()}`

          // Interactive notifications
          if (['challenge', 'game_invite', 'tournament'].includes(type)) {
              addNotification({
                  id,
                  type,
                  title: data.title || 'Chess Challenge!',
                  message: data.message || 'You have received a challenge',
                  hasActions: true,
                  data: data.data || data 
              })
          } else {
              // Passive notifications
              addNotification({
                  id,
                  type,
                  title: data.title,
                  message: data.message,
                  hasActions: false
              })
              // Also show toast for passive ones
              showToast(data.message || 'New notification', 'info')
          }
      }

      // Use modern subscribe
      const unsubNotification = subscribe('notification', handleNotification)
      const unsubGameStarted = subscribe('game_started', handleNotification)
      
      return () => {
          unsubNotification()
          unsubGameStarted()
      }
  }, [subscribe, addNotification, showToast, navigate])


  // --- Actions ---

  const handleAction = useCallback(async (actionName, apiCallFn, successMsg, eventToDispatch) => {
      try {
          const result = await apiCallFn()
          if (successMsg) showToast(successMsg, 'success')
          if (eventToDispatch) {
            // Support both old custom events and new navigation
             if (eventToDispatch.type === 'navigate-to-game' && window.navigateToGame) {
                 window.navigateToGame(eventToDispatch.detail.gameId)
             } else {
                 window.dispatchEvent(new CustomEvent(eventToDispatch.type, { detail: eventToDispatch.detail }))
             }
          }
          return result
      } catch (err) {
          console.error(`Error in ${actionName}:`, err)
          showToast(`Failed: ${err.message}`, 'error')
          throw err
      }
  }, [showToast])

  const acceptChallenge = useCallback((notification) => {
      const gameId = notification.data?.game_id
      if (!gameId) return
      
      return handleAction(
          'acceptChallenge',
          () => api(`/games/${gameId}/join`, { method: 'PUT' }),
          'Challenge accepted! Starting game...',
          { type: 'navigate-to-game', detail: { gameId } }
      )
  }, [api, handleAction])

  const declineChallenge = useCallback((notification) => {
      const gameId = notification.data?.game_id
      if (!gameId) return

      return handleAction(
          'declineChallenge',
          () => api(`/games/${gameId}/challenge`, { method: 'DELETE' }),
          'Challenge declined',
          null
      )
  }, [api, handleAction])

  const acceptTournamentInvite = useCallback((notification) => {
      const tournamentId = notification.data?.tournament_id || notification.data?.id
      if (!tournamentId) return

      return handleAction(
          'acceptTournamentInvite',
          () => api(`/tournaments/${tournamentId}/join`, { method: 'POST' }),
          'Joined tournament successfully!',
          { type: 'navigate-to-tournament', detail: { tournamentId } }
      )
  }, [api, handleAction])

  const declineTournamentInvite = useCallback((notification) => {
      const tournamentId = notification.data?.tournament_id || notification.data?.id
      if (!tournamentId) return

      return handleAction(
          'declineTournamentInvite',
          () => api(`/tournaments/${tournamentId}/invite/decline`, { method: 'POST' }),
          'Tournament invitation declined',
          null
      )
  }, [api, handleAction])

  const acceptFriendRequest = useCallback((notification) => {
      const requestId = notification.data?.request_id || notification.data?.id
      if (!requestId) return

      return handleAction(
          'acceptFriendRequest',
          () => api(`/friends/requests/${requestId}/accept`, { method: 'POST' }),
          'Friend request accepted!',
          { type: 'friend-request-updated', detail: {} }
      )
  }, [api, handleAction])

  const declineFriendRequest = useCallback((notification) => {
      const requestId = notification.data?.request_id || notification.data?.id
      if (!requestId) return

      return handleAction(
          'declineFriendRequest',
          () => api(`/friends/requests/${requestId}/decline`, { method: 'POST' }),
          'Friend request declined',
          { type: 'friend-request-updated', detail: {} }
      )
  }, [api, handleAction])

  const value = useMemo(() => ({
    notifications,
    addNotification,
    removeNotification,
    acceptChallenge,
    declineChallenge,
    acceptTournamentInvite,
    declineTournamentInvite,
    acceptFriendRequest,
    declineFriendRequest
  }), [
    notifications, addNotification, removeNotification,
    acceptChallenge, declineChallenge,
    acceptTournamentInvite, declineTournamentInvite,
    acceptFriendRequest, declineFriendRequest
  ])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}